import { useCallback } from 'react';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { useRemoveRetailPriceRangeFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { useTopicBuilderFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import { TopicFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';

/**
 * Mapping of TopicFilter to AdvancedSearchRequestBuilder term filter
 */
const topicFilterToTermFilter = {
  [TopicFilter.IncludedKeywordPhrases]: { fieldName: 'searchTermFuzzy', condition: 'should' },
  [TopicFilter.ExcludedKeywordPhrases]: { fieldName: 'excludedSearchTermFuzzy', condition: 'must_not' },
  [TopicFilter.IncludedKeywords]: { fieldName: 'searchTerm', condition: 'should' },
  [TopicFilter.ExcludedKeywords]: { fieldName: 'excludedSearchTerm', condition: 'must_not' },
  [TopicFilter.IncludedBrandIds]: { fieldName: 'brandId', condition: 'should' },
  [TopicFilter.ExcludedBrandIds]: { fieldName: 'excludedBrandId', condition: 'must_not' },
  [TopicFilter.IncludedCategoryIds]: { fieldName: 'categoryId', condition: 'should' },
  [TopicFilter.ExcludedCategoryIds]: { fieldName: 'excludedCategoryId', condition: 'must_not' },
  [TopicFilter.IncludedSubCategoryIds]: { fieldName: 'subCategoryId', condition: 'should' },
  [TopicFilter.ExcludedSubCategoryIds]: { fieldName: 'excludedSubCategoryId', condition: 'must_not' }
};

/**
 * Takes in the filters from the TopicBuilderStore and applies them to an advanced search request
 */
export default function useTopicAdvancedRequestBuilder(indexName: string) {
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters(indexName);

  const { filters } = useTopicBuilderFilters();
  /**
   * Collection of active term filters mapped from the form. These are ready for use in AS queries.
   */
  const activeFilters = Object.entries(filters).reduce((acc, [filter, values]) => {
    const termFilters = {
      fieldName: topicFilterToTermFilter[filter].fieldName,
      condition: topicFilterToTermFilter[filter].condition,
      values
    };
    return [...acc, termFilters];
  }, []);

  return useCallback(
    (additionalBuilder?: (builder: AdvancedSearchRequestBuilder) => AdvancedSearchRequestBuilder) =>
      (requestBuilder: AdvancedSearchRequestBuilder) => {
        activeFilters.forEach(({ fieldName, condition, values }) => {
          requestBuilder.addConditionTermFilter(fieldName, condition, values);
        });

        // We don't want to see search results for the Other Traffic keyword
        requestBuilder
          .removeConditionTermFilters(
            (filter) => filter.fieldName === 'excludedSearchTerm' && filter.values.includes('[Other Traffic]')
          )
          .addConditionTermFilter('excludedSearchTerm', 'must_not', ['[Other Traffic]'])
          .apply(removeRetailPriceRangeFilters);

        if (additionalBuilder) {
          return additionalBuilder(requestBuilder);
        }
        return requestBuilder;
      },
    [activeFilters, removeRetailPriceRangeFilters]
  );
}
